import { findElement } from './dom.js';

let wakeLock: WakeLockSentinel | null = null;
let releaseTimeout: ReturnType<typeof setTimeout> | null = null;
let isAudioOrVideoPlaying = false;

function updateBadge(active: boolean) {
  const badge = findElement('wake-lock-badge', HTMLElement);
  const sep = findElement('wake-lock-sep', HTMLElement);
  if (badge) {
    badge.style.display = active ? 'inline-flex' : 'none';
  }
  if (sep) {
    sep.style.display = active ? 'inline' : 'none';
  }
}

/**
 * Requests a screen wake lock to keep the display on during playback.
 */
export async function acquireWakeLock(): Promise<boolean> {
  if (releaseTimeout) {
    clearTimeout(releaseTimeout);
    releaseTimeout = null;
  }
  isAudioOrVideoPlaying = true;

  if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
    return false;
  }

  if (document.visibilityState !== 'visible') {
    return false;
  }

  if (wakeLock && !wakeLock.released) {
    updateBadge(true);
    return true;
  }

  try {
    wakeLock = await navigator.wakeLock.request('screen');
    wakeLock.addEventListener('release', () => {
      if (wakeLock && wakeLock.released) {
        wakeLock = null;
      }
      if (!wakeLock) {
        updateBadge(false);
      }
    });
    updateBadge(true);
    console.log('[WakeLock] Écran maintenu actif pendant la lecture');
    return true;
  } catch (err) {
    console.warn('[WakeLock] Impossible d\'activer le Wake Lock:', err);
    updateBadge(false);
    return false;
  }
}

/**
 * Releases the screen wake lock when playback stops or is paused.
 * If immediate is false, uses a small debounce to avoid flickering between tracks.
 */
export async function releaseWakeLock(immediate = false): Promise<void> {
  const doRelease = async () => {
    isAudioOrVideoPlaying = false;
    if (wakeLock) {
      try {
        await wakeLock.release();
      } catch {
        // Sentinel may already have been released by browser
      } finally {
        wakeLock = null;
      }
    }
    updateBadge(false);
  };

  if (immediate) {
    if (releaseTimeout) {
      clearTimeout(releaseTimeout);
      releaseTimeout = null;
    }
    await doRelease();
  } else {
    if (releaseTimeout) clearTimeout(releaseTimeout);
    releaseTimeout = setTimeout(() => {
      releaseTimeout = null;
      doRelease();
    }, 800);
  }
}

/**
 * Returns whether wake lock is currently active.
 */
export function isWakeLockActive(): boolean {
  return Boolean(wakeLock && !wakeLock.released);
}

/**
 * Initializes wake lock listeners on the audio player, document visibility, and iframe messages.
 */
export function initWakeLock(audioPlayer: HTMLAudioElement | null, ytPlayer?: HTMLIFrameElement | null) {
  if (audioPlayer) {
    audioPlayer.addEventListener('play', () => {
      acquireWakeLock();
    });

    audioPlayer.addEventListener('playing', () => {
      acquireWakeLock();
    });

    audioPlayer.addEventListener('pause', () => {
      releaseWakeLock(false);
    });

    audioPlayer.addEventListener('ended', () => {
      releaseWakeLock(false);
    });

    audioPlayer.addEventListener('emptied', () => {
      releaseWakeLock(false);
    });

    audioPlayer.addEventListener('error', () => {
      releaseWakeLock(true);
    });
  }

  // Handle visibility change:
  // Browsers automatically release wake lock when document becomes hidden.
  // When visible again, re-acquire if audio is still playing!
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const isAudioPlaying = audioPlayer && !audioPlayer.paused && !audioPlayer.ended && audioPlayer.currentTime > 0;
      if (isAudioPlaying || isAudioOrVideoPlaying) {
        acquireWakeLock();
      }
    }
  });

  // Re-check on focus
  window.addEventListener('focus', () => {
    if (document.visibilityState === 'visible') {
      const isAudioPlaying = audioPlayer && !audioPlayer.paused && !audioPlayer.ended && audioPlayer.currentTime > 0;
      if (isAudioPlaying || isAudioOrVideoPlaying) {
        acquireWakeLock();
      }
    }
  });

  // Handle YouTube iframe messages
  window.addEventListener('message', (event) => {
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      if (data && typeof data === 'object') {
        if (data.event === 'onStateChange') {
          if (data.info === 1) { // Playing
            acquireWakeLock();
          } else if (data.info === 2 || data.info === 0) { // Paused or Ended
            releaseWakeLock(false);
          }
        }
      }
    } catch {}
  });

  // If audio is already playing on init
  if (audioPlayer && !audioPlayer.paused && audioPlayer.currentTime > 0) {
    acquireWakeLock();
  }
}
